import { useCallback, useMemo, useSyncExternalStore } from 'react';
import type * as Y from 'yjs';
import type { BoardData, CollectionName, EntityOf } from '@stageplottter/core';
import { collection, toBoardData } from '@stageplottter/core';
import { freeformGroupsMap } from '../editor/model/groups';
import type { LeaseClient } from '../state/leaseClient';
import type { LeaseState } from '../state/lease';
import { peersFromStates } from '../state/presence';
import type { Peer } from '../state/presence';
import type { AwarenessLike, BoardSession, SessionStatus } from './session';

/**
 * Snapshot cache per Y.Doc: the BoardData projection is recomputed lazily once per document
 * update and shared by every subscriber. Entity objects keep their identity between snapshots
 * when unchanged (Y.Map returns the stored JSON value), so React.memo on entities works.
 */
interface DocCache {
  version: number;
  data: BoardData | null;
  dataVersion: number;
  groups: Record<string, string> | null;
  groupsVersion: number;
  listeners: Set<() => void>;
}

const caches = new WeakMap<Y.Doc, DocCache>();

function cacheFor(doc: Y.Doc): DocCache {
  let c = caches.get(doc);
  if (!c) {
    const cache: DocCache = {
      version: 0,
      data: null,
      dataVersion: -1,
      groups: null,
      groupsVersion: -1,
      listeners: new Set(),
    };
    doc.on('update', () => {
      cache.version += 1;
      for (const l of [...cache.listeners]) l();
    });
    caches.set(doc, cache);
    c = cache;
  }
  return c;
}

export function subscribeDoc(doc: Y.Doc, listener: () => void): () => void {
  const c = cacheFor(doc);
  c.listeners.add(listener);
  return () => {
    c.listeners.delete(listener);
  };
}

export function boardSnapshot(doc: Y.Doc): BoardData {
  const c = cacheFor(doc);
  if (c.data === null || c.dataVersion !== c.version) {
    c.data = toBoardData(doc);
    c.dataVersion = c.version;
  }
  return c.data;
}

export function freeformGroupsSnapshot(doc: Y.Doc): Record<string, string> {
  const c = cacheFor(doc);
  if (c.groups === null || c.groupsVersion !== c.version) {
    const next = Object.fromEntries(freeformGroupsMap(doc).entries());
    const prev = c.groups;
    const same =
      prev !== null &&
      Object.keys(prev).length === Object.keys(next).length &&
      Object.entries(next).every(([k, v]) => prev[k] === v);
    c.groups = same ? prev : next;
    c.groupsVersion = c.version;
  }
  return c.groups!;
}

/** Whole board as plain JSON, re-rendering on every document change. */
export function useBoardData(doc: Y.Doc): BoardData {
  const subscribe = useCallback((l: () => void) => subscribeDoc(doc, l), [doc]);
  const get = useCallback(() => boardSnapshot(doc), [doc]);
  return useSyncExternalStore(subscribe, get, get);
}

export function useFreeformGroups(doc: Y.Doc): Record<string, string> {
  const subscribe = useCallback((l: () => void) => subscribeDoc(doc, l), [doc]);
  const get = useCallback(() => freeformGroupsSnapshot(doc), [doc]);
  return useSyncExternalStore(subscribe, get, get);
}

/** One entity; only re-renders when that entity changes. */
export function useEntity<C extends CollectionName>(
  doc: Y.Doc,
  name: C,
  id: string | null | undefined,
): EntityOf<C> | undefined {
  const map = useMemo(() => collection(doc, name), [doc, name]);
  const subscribe = useCallback(
    (l: () => void) => {
      const handler = (e: Y.YMapEvent<EntityOf<C>>) => {
        if (id && e.keysChanged.has(id)) l();
      };
      map.observe(handler);
      return () => map.unobserve(handler);
    },
    [map, id],
  );
  const get = useCallback(() => (id ? map.get(id) : undefined), [map, id]);
  return useSyncExternalStore(subscribe, get, get);
}

export function useSessionStatus(session: BoardSession): SessionStatus {
  return useSyncExternalStore(session.subscribe, session.getStatus, session.getStatus);
}

export function useLeaseState(client: LeaseClient): LeaseState {
  return useSyncExternalStore(client.subscribe, client.getState, client.getState);
}

const NO_PEERS: Peer[] = [];

function createPeersStore(awareness: AwarenessLike | null, selfUserId: string) {
  let cached: Peer[] = NO_PEERS;
  let dirty = true;
  return {
    subscribe(l: () => void) {
      if (!awareness) return () => undefined;
      const h = () => {
        dirty = true;
        l();
      };
      awareness.on('change', h);
      return () => awareness.off('change', h);
    },
    get(): Peer[] {
      if (!awareness) return NO_PEERS;
      if (dirty) {
        cached = peersFromStates(awareness.getStates(), awareness.clientID, selfUserId);
        dirty = false;
      }
      return cached;
    },
  };
}

/** Remote peers (members and guests) with names, colours and cursors. */
export function usePeers(awareness: AwarenessLike | null, selfUserId: string): Peer[] {
  const store = useMemo(() => createPeersStore(awareness, selfUserId), [awareness, selfUserId]);
  return useSyncExternalStore(store.subscribe, store.get, store.get);
}

export interface UndoState {
  canUndo: boolean;
  canRedo: boolean;
}

function createUndoStore(undo: Y.UndoManager) {
  let cached: UndoState = { canUndo: false, canRedo: false };
  return {
    subscribe(l: () => void) {
      const events = ['stack-item-added', 'stack-item-popped', 'stack-cleared'] as const;
      for (const e of events) undo.on(e, l);
      return () => {
        for (const e of events) undo.off(e, l);
      };
    },
    get(): UndoState {
      const next = { canUndo: undo.canUndo(), canRedo: undo.canRedo() };
      if (next.canUndo !== cached.canUndo || next.canRedo !== cached.canRedo) cached = next;
      return cached;
    },
  };
}

export function useUndoState(undo: Y.UndoManager): UndoState {
  const store = useMemo(() => createUndoStore(undo), [undo]);
  return useSyncExternalStore(store.subscribe, store.get, store.get);
}
